import SectionMessage from '@atlaskit/section-message';
import {
  LinkClickedEvent,
  LozengeClickedEvent,
  MarketplaceAnalyticsContext,
  MenuExpandedEvent,
  MenuItemClickedEvent,
  TabClickedEvent,
  useAnalyticsEvents,
} from '@atlassian/mpac-analytics';
import {
  AppListing,
  AppListingTab,
  ELEMENT_ID,
  MediaViewerResource,
} from '@atlassian/mpac-app-listing';
import { useFeatureFlags, useStatsig } from '@atlassian/mpac-feature-flags';
import { BodyNormal, LinkWithIcon } from '@atlassian/mpac-primitives';
import Telemetry from '@atlassian/mpac-telemetry';
import { AppDeployment, HostingTypeToQueryParam } from '@atlassian/mpac-types';
import {
  AtlassianProductHostingType,
  MarketplaceAppPaymentModel,
} from '@atlassian/mpac-types/graphql-gateway-types';
import { ExternalLink, PartnerPermissions } from '@atlassian/mpac-ui';
import { DOMUtils, MarketplaceAppUtils, UrlUtils } from '@atlassian/mpac-utils';
import capitalize from 'lodash/capitalize';
import React, { useLayoutEffect } from 'react';

import {
  safeToForwardToPartner,
  trackDownloadLink,
  trackLightboxView,
  trackLookingForDataCenter,
  trackSeeMoreReviews,
  trackVendorSupportLinkClick,
  trackWatchAction,
} from '../../../../../../../../common/utils/analytics/analytics';
import {
  useAppListingPageAnalytics,
  useGoogleAnalyticsExtraContext,
  useVendorGoogleAnalytics,
} from '../../../../../../../../hooks';
import {
  ANALYTICS_CATEGORY_APP_LISTING,
  ANALYTICS_EVENT_CONTAINER_APP_HEADER,
  ANALYTICS_EVENT_CONTAINER_NAV_BAR,
  ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
  ANALYTICS_SUBJECT_ID_APP_LISTING_TAB,
  ANALYTICS_SUBJECT_ID_CLOUD_MIGRATION_INFORMATION,
  ANALYTICS_SUBJECT_ID_CONTACT_VENDOR,
  ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
  ANALYTICS_SUBJECT_ID_MIGRATION_ASSISTANCE,
} from '../../../../../../../analytics/Events';
import BootstrapConfig from '../../../../../../../bootstrapConfig';
import { TEST_IDS } from '../../../../../../../common/testIds';
import {
  EXPLORE_CLOUD_MIGRATION_LINK,
  SERVER_EOL_MESSAGE_FOR_DATA_CENTER_SUPPORTED_APPS,
  SERVER_EOL_MESSAGE_FOR_NON_DATA_CENTER_SUPPORTED_APPS,
  SERVER_EOS_MESSAGE,
} from '../../../../../../../constants/ServerEoS';
import useCloudMigrationInfo from '../../../../../../../hooks/useCloudMigration';
import useLoggedInUser from '../../../../../../../hooks/useLoggedInUser';
import AdminHeaderBanners from '../AdminHeaderBanners/AdminHeaderBanners';
import AdminHeaderLinks from '../AdminHeaderLinks';
import AppListingJSDWidget from '../AppListingJSDWidget';
import AppListingMeta from '../AppListingMeta/AppListingMeta';
import {
  getEmceeAppListingInstallLink,
  getEmceeAppListingRequestAppLink,
  onlyCompatibleProducts,
} from '../AppsRoute.utils';
import HeaderInjection from '../HeaderInjection';
import SiteSelectorDialogWrapper from '../HeaderInjection/SiteSelectorDialogWrapper';
import { SITE_SELECTOR_VISIBLE_QUERY_PARAM, useCTAClickHandler } from '../hooks/useCTAClickHandler';
import usePartnerPermissions from '../hooks/usePartnerPermissions';
import {
  getMigrationAssistanceStatus,
  getMigrationInformationAvailable,
  hasNewerPrivateVersion,
  toAnalyticsHosting,
} from '../utils';
import { WarningContainerAfterHeader } from './styles';
import { serverEOLAppsWithDcVersionAvailable } from './utils';

import type {
  AppDetailsFromSearchResponse,
  AppListingTabsType,
  AppVersion,
  HostingOptionKindType,
  MarketplaceAppByKeyQuery,
  MediaViewerResourceType,
  SupportResourcesLinkType,
} from '@atlassian/mpac-app-listing';
import type {
  AppListingCloudMigration,
  AppListingUser,
  BootstrapLinks,
} from '@atlassian/mpac-types';

type BeforeHeaderContentProps = {
  appId: string;
  links: BootstrapLinks;
  appDetails: NonNullable<MarketplaceAppByKeyQuery['marketplaceAppByKey']>;
  showOnlyPublic: boolean;
};

const BeforeHeaderContent = ({
  links,
  appId,
  appDetails,
  showOnlyPublic,
}: BeforeHeaderContentProps) => {
  const hasNewerPvtVersionForAnyHosting =
    hasNewerPrivateVersion(appDetails.privateCloudVersion, appDetails.publicCloudVersion) ||
    hasNewerPrivateVersion(appDetails.privateServerVersion, appDetails.publicServerVersion) ||
    hasNewerPrivateVersion(appDetails.privateDataCenterVersion, appDetails.publicDataCenterVersion);

  return (
    <>
      <AdminHeaderBanners
        appManageLink={links.appManageLink.expand({ appId })}
        hasNewerPrivateVersion={hasNewerPvtVersionForAnyHosting}
        listingStatus={appDetails.listingStatus}
        showOnlyPublic={showOnlyPublic}
      />
      <AdminHeaderLinks
        appManageLink={links.appManageLink.expand({ appId })}
        appShortLink={links.appShortLink.expand({ appId })}
      />
    </>
  );
};

type AppListingPageContentProps = {
  appDetails: NonNullable<MarketplaceAppByKeyQuery['marketplaceAppByKey']>;
  appDetailsFromSearchResponse: AppDetailsFromSearchResponse;
  appId: string;
  setNewTab: (tab: AppListingTabsType) => void;
  setNewHostingType: (hostingType: AtlassianProductHostingType) => void;
  selectedHosting: AtlassianProductHostingType;
  selectedAppVersion: AppVersion;
  tab: AppListingTabsType;
  links: BootstrapLinks;
  showOnlyPublic: boolean;
};
// eslint-disable-next-line max-lines-per-function
const AppListingPageContent = (
  props: AppListingPageContentProps & {
    cloudMigrationInfo: AppListingCloudMigration | null | undefined;
  }
) => {
  const { isLoggedIn, isAdmin, userAvatarUrl, user } = useLoggedInUser();
  const {
    appDetails,
    appDetailsFromSearchResponse,
    appId,
    selectedHosting,
    selectedAppVersion,
    tab,
    showOnlyPublic,
    links,
    cloudMigrationInfo,
  } = props;
  const {
    mpacBaseUrl,
    jiraServiceDeskEmbedBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
    feedbackCollectorApiKeys,
    feedbackGatewayBaseUrl,
  } = BootstrapConfig.get();
  const loginHref = `/login?redirect=${encodeURIComponent(window.location.href)}`;

  const { enableAetherAppListingPage } = useStatsig();
  const showAetherAppListingPage = enableAetherAppListingPage();

  const fireAnalyticsEvent = useAnalyticsEvents();
  const { enableGoogleAnalytics4Tracking, enableMpacServerEolWarning } = useFeatureFlags();
  const { permissions } = usePartnerPermissions(mpacBaseUrl, appDetails.partner?.id, isLoggedIn);

  const serverAppVersion = appDetails.serverAppVersions.edges?.[0]?.node;
  const dataCenterAppVersion = appDetails.dataCenterAppVersions.edges?.[0]?.node;
  const cloudAppVersion = appDetails.cloudAppVersions.edges?.[0]?.node;
  const versionInfo = {
    cloudAppVersion,
    serverAppVersion,
    dataCenterAppVersion,
  };

  useAppListingPageAnalytics({
    hostingType: selectedHosting,
    pricingModel: selectedAppVersion.paymentModel,
    appDetails,
  });

  useVendorGoogleAnalytics({
    appId,
    appKey: appDetails.appKey,
    googleAnalyticsId: appDetails.googleAnalyticsId,
    buildNumber: selectedAppVersion.buildNumber,
    appName: appDetails.name,
  });

  // Track using Google analytics 4 additionally if feature flag is on and ga4 id is present.
  // Once ga4 migration is complete, the flag and ga3 tracking will be removed.
  // https://ecosystem.atlassian.net/browse/AMKT-29181
  useVendorGoogleAnalytics({
    appId,
    appKey: appDetails.appKey,
    googleAnalyticsId: enableGoogleAnalytics4Tracking ? appDetails.googleAnalytics4Id : null,
    buildNumber: selectedAppVersion.buildNumber,
    appName: appDetails.name,
  });

  useGoogleAnalyticsExtraContext({
    cloudAppVersion,
    serverAppVersion,
    dataCenterAppVersion,
  });

  useLayoutEffect(() => {
    Telemetry.timeToFirstMeaningfulPaint(performance.now());
  }, []);

  const instrumentHostingClick = (
    hosting: AtlassianProductHostingType,
    hostingOptionKind: HostingOptionKindType
  ) => {
    fireAnalyticsEvent({
      eventType: MenuItemClickedEvent,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        label: toAnalyticsHosting(hosting),
        hosting: toAnalyticsHosting(hosting),
        selectionType: hostingOptionKind,
      },
    });
  };

  const handleTabChange = (newTab: AppListingTabsType) => {
    props.setNewTab(newTab);
    document?.body?.scrollTo?.({ left: 0, top: 0, behavior: 'smooth' });
    fireAnalyticsEvent({
      eventType: TabClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_APP_LISTING_TAB,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        label: capitalize(newTab),
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        appListingTabKey: capitalize(newTab),
      },
    });
  };

  const handleHostingChange = (hosting: AtlassianProductHostingType) => {
    props.setNewHostingType(hosting);
  };

  const handleVendorContactClick = (hostingType: string) => {
    fireAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_CONTACT_VENDOR,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        source: hostingType,
      },
    });
  };

  const handleCloudMigrationResourcesLinksClick = (label: string) => {
    fireAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_CLOUD_MIGRATION_INFORMATION,
      source: ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
        label,
      },
    });
  };

  const handleSupportLinkClick = (supportLink: SupportResourcesLinkType) => {
    safeToForwardToPartner(appId, () => {
      trackVendorSupportLinkClick(supportLink);
    });
  };

  const handleMediaViewerOpen = (resourceType: MediaViewerResourceType, imageUrl: string) => {
    safeToForwardToPartner(appId, () => {
      trackLightboxView(imageUrl, resourceType === MediaViewerResource.Highlight, mpacCdnBaseUrl);
    });
  };

  const handleWatchAppToggle = (toggleOn: boolean) => {
    safeToForwardToPartner(appId, () => {
      trackWatchAction(toggleOn ? 'Watch' : 'Unwatch');
    });
  };

  const handleSeeMoreReviewsClick = () => {
    safeToForwardToPartner(appId, () => {
      trackSeeMoreReviews();
    });
  };

  const handleArtifactDownloadClick = (url: string) => {
    const { pathname } = new URL(url);
    safeToForwardToPartner(appId, () => {
      trackDownloadLink(pathname);
    });
  };

  const handleHostingSelectOpen = () => {
    fireAnalyticsEvent({
      eventType: MenuExpandedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        hostingOptions: appDetails.productHostingOptions.map(toAnalyticsHosting),
      },
    });
  };

  const handleHostingOptionClick = (
    hosting: AtlassianProductHostingType,
    hostingOptionKind: HostingOptionKindType
  ) => {
    instrumentHostingClick(hosting, hostingOptionKind);

    if (hosting === AtlassianProductHostingType.DataCenter) {
      safeToForwardToPartner(appId, () => {
        trackLookingForDataCenter();
      });
    }
  };

  const handleMigrationLozengeClick = () => {
    handleTabChange(AppListingTab.Overview);
    setTimeout(() => {
      DOMUtils.scrollToAndFocusElement(`#${ELEMENT_ID.CLOUD_MIGRATION_SECTION_LINKS}`);
    }, 0);
    fireAnalyticsEvent({
      eventType: LozengeClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_MIGRATION_ASSISTANCE,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
      },
    });
  };

  const {
    siteSelectorOpen,
    onSiteSelectorClose,
    hostingSelectorOpen,
    onHostingSelectorClose,
    activeCTAKind,
    hostingSelectorPurchaseIntent,
    onRequestAppClick,
    onAppListingHeaderCTAClick,
    onHostingSelectorCTAClick,
    onPricingCalculatorCTAClick,
  } = useCTAClickHandler({
    appKey: appDetails.appKey,
    appId,
    userLoggedIn: isLoggedIn,
    userEmail: user?.email,
    selectedHosting,
    paymentModel: selectedAppVersion.paymentModel,
    compatibleProducts: selectedAppVersion.deployment.compatibleProducts,
    cloudAppVersion,
    artifactDownloadLink: links.artifactDownload,
  });

  const getHeaderInjection = () => {
    const hasPublicCloudVersion = !!appDetails.publicCloudVersion.edges?.length;
    const hasPrivateCloudVersion = !!appDetails.privateCloudVersion.edges?.length;

    // An app is private remoteable only, if it has no public version
    // and at-least one private version
    const isPrivateRemoteableOnly = !hasPublicCloudVersion && hasPrivateCloudVersion;
    return (
      <HeaderInjection
        appKey={appDetails.appKey}
        appId={appId}
        appName={appDetails.name}
        selectedHosting={selectedHosting}
        privateListingsLink={links.managePrivateListing}
        privateRemotableOnly={isPrivateRemoteableOnly}
        appDetailsLink={links.appDetails}
        artifactDownloadLink={links.artifactDownload}
        trialLicenseActivationLink={links.trialLicenseActivation}
        licensePurchaseLink={links.licensePurchase}
        versionInfo={versionInfo}
        productHostingOptions={appDetails.productHostingOptions}
        hostingSelectorOpen={hostingSelectorOpen}
        onHostingSelectorClose={onHostingSelectorClose}
        activeCTAKind={activeCTAKind}
        hostingSelectorPurchaseIntent={hostingSelectorPurchaseIntent}
        onRequestAppClick={onRequestAppClick}
        onAppListingHeaderCTAClick={onAppListingHeaderCTAClick}
        onHostingSelectorCTAClick={onHostingSelectorCTAClick}
        watchersInfo={appDetails.watchersInfo}
        isLoggedIn={isLoggedIn}
        loginHref={loginHref}
      />
    );
  };

  const renderBeforeHeader = () => {
    return (
      <BeforeHeaderContent
        appId={appId}
        appDetails={appDetails}
        links={links}
        showOnlyPublic={showOnlyPublic}
      />
    );
  };

  const contentAfterHeader = () => {
    if (isServerAppEos) return SERVER_EOS_MESSAGE;
    else if (showServerEolWarning) {
      return inAllowListDataCenterSupportedApps
        ? SERVER_EOL_MESSAGE_FOR_DATA_CENTER_SUPPORTED_APPS
        : SERVER_EOL_MESSAGE_FOR_NON_DATA_CENTER_SUPPORTED_APPS;
    } else return '';
  };

  const inAllowListDataCenterSupportedApps = Boolean(
    serverEOLAppsWithDcVersionAvailable?.includes(appDetails.appKey)
  );

  const renderAfterHeader = () => {
    const SectionMessageBody = showAetherAppListingPage ? BodyNormal : 'p';
    const actionLink = showAetherAppListingPage ? (
      <LinkWithIcon
        size="medium"
        type="external"
        href={EXPLORE_CLOUD_MIGRATION_LINK}
        aria-label="Explore migration program"
      >
        Explore migration program
      </LinkWithIcon>
    ) : (
      <ExternalLink key="atlassian-migration-program" href={EXPLORE_CLOUD_MIGRATION_LINK}>
        Explore migration program
      </ExternalLink>
    );

    if (isServerAppEos || showServerEolWarning)
      return (
        <WarningContainerAfterHeader
          data-testid={isServerAppEos ? TEST_IDS.SERVER_EOS_MESSAGE : TEST_IDS.SERVER_EOL_MESSAGE}
        >
          <SectionMessage
            appearance={showAetherAppListingPage ? 'information' : 'warning'}
            actions={[actionLink]}
          >
            <SectionMessageBody>{contentAfterHeader()}</SectionMessageBody>
          </SectionMessage>
        </WarningContainerAfterHeader>
      );
  };

  const metaTagImage =
    selectedAppVersion.heroImage ||
    (selectedAppVersion.highlights?.length && selectedAppVersion.highlights[0].croppedScreenshot) ||
    appDetails.logo;

  const cloudAppCompatibilities = cloudAppVersion
    ? onlyCompatibleProducts(selectedAppVersion.deployment.compatibleProducts).map(
        (product) => product.id
      )
    : [];

  const installAppHrefResolver = (selectedSiteProduct) =>
    getEmceeAppListingInstallLink(appDetails.appKey, selectedSiteProduct, user?.email);

  const requestAppHrefResolver = (selectedSiteProduct) =>
    getEmceeAppListingRequestAppLink(appDetails.appKey, selectedSiteProduct, user?.email);

  const loginRedirectPath = UrlUtils.updateQueryParam(
    window.location.href,
    SITE_SELECTOR_VISIBLE_QUERY_PARAM,
    'true'
  );
  const appListingUser: AppListingUser = {
    isMpacAdmin: isAdmin,
    isLoggedIn,
    avatarUrl: userAvatarUrl,
    userId: user?.id,
  };

  const { showInstallationTab, showServerEolWarning, serverEolStartDate } =
    MarketplaceAppUtils.isServerAppEol(
      selectedHosting === AtlassianProductHostingType.Server,
      selectedAppVersion?.deployment?.compatibleProducts,
      { enableMpacServerEolWarning }
    );

  const isServerAppEos = MarketplaceAppUtils.isServerAppEos(
    selectedHosting === AtlassianProductHostingType.Server,
    selectedAppVersion?.deployment?.compatibleProducts
  );

  return (
    <>
      <AppListingMeta
        appId={appDetails.appId}
        appName={appDetails.name}
        appSlug={appDetails.slug}
        appDescription={appDetails.tagline}
        canonicalLink={links.appListingCanonical}
        mediaImageLink={links.mediaImage}
        image={metaTagImage}
        logo={appDetails.logo}
      />
      <SiteSelectorDialogWrapper
        appId={appDetails.appId}
        installAppHrefResolver={installAppHrefResolver}
        requestAppHrefResolver={requestAppHrefResolver}
        appCompatibilities={cloudAppCompatibilities}
        appKey={appDetails.appKey}
        appName={appDetails.name}
        paymentModel={selectedAppVersion.paymentModel}
        appDetailsLink={links.appDetails}
        onClose={onSiteSelectorClose}
        isOpen={siteSelectorOpen}
        loginRedirectPath={loginRedirectPath}
        mpacBaseUrl={mpacBaseUrl}
      />
      <PartnerPermissions.Provider value={permissions}>
        <AppListing
          appDetails={appDetails}
          appDetailsFromSearchResponse={appDetailsFromSearchResponse}
          components={{
            beforeHeader: renderBeforeHeader(),
            afterHeader: renderAfterHeader(),
          }}
          user={appListingUser}
          loginHref={loginHref}
          mpacBaseUrl={mpacBaseUrl}
          feedbackCollectorApiKeys={feedbackCollectorApiKeys}
          feedbackGatewayBaseUrl={feedbackGatewayBaseUrl}
          mpacCdnBaseUrl={mpacCdnBaseUrl}
          atlassianCommunityBaseUrl={atlassianCommunityBaseUrl}
          headerInjection={getHeaderInjection()}
          links={links}
          selectedHosting={selectedHosting}
          selectedTab={tab}
          cloudMigration={cloudMigrationInfo}
          onHostingChange={handleHostingChange}
          onTabSelect={handleTabChange}
          onSupportLinkClick={handleSupportLinkClick}
          onMediaViewerOpen={handleMediaViewerOpen}
          onWatchAppToggle={handleWatchAppToggle}
          onSeeMoreReviewsClick={handleSeeMoreReviewsClick}
          onArtifactDownloadClick={handleArtifactDownloadClick}
          onHostingSelectOpen={handleHostingSelectOpen}
          onHostingOptionClick={handleHostingOptionClick}
          onContactPartnerClick={handleVendorContactClick}
          onCloudMigrationResourceLinksClick={handleCloudMigrationResourcesLinksClick}
          onMigrationLozengeClick={handleMigrationLozengeClick}
          openMarketplaceLinksInNewTab={false}
          showHostingSelect
          showResourcesSection
          showInstallationTab={showInstallationTab}
          serverEolStartDate={serverEolStartDate}
          showVersionsTab
          showReviewsTab
          showInstallationCount
          showIntegrationsDetailsSection
          showPricingTab
          showHeaderProductCompatibilities
          showHeaderLozenges
          showCloudMigrationLozenge={true}
          onPricingCalculatorInstallCTAClick={onPricingCalculatorCTAClick}
          isServerAppEos={isServerAppEos}
        />
      </PartnerPermissions.Provider>
      {!__REACT_SSR__ &&
        props.appDetails.jsdWidgetKey &&
        !DOMUtils.isCrawler(window.navigator.userAgent) && (
          <AppListingJSDWidget
            jiraServiceDeskEmbedBaseUrl={jiraServiceDeskEmbedBaseUrl}
            jsdWidgetKey={props.appDetails.jsdWidgetKey}
          />
        )}
    </>
  );
};

const AppListingWithAnalyticsContext = (props: AppListingPageContentProps) => {
  const { appDetails, appId, selectedHosting, tab, selectedAppVersion } = props;
  const hosting = HostingTypeToQueryParam[selectedHosting];
  const { appKey } = appDetails || {};
  const paymentModel =
    selectedAppVersion.paymentModel === MarketplaceAppPaymentModel.Free ? 'free' : 'paid';
  const isEcosystemCloudApp =
    selectedAppVersion.deployment.__typename === AppDeployment.ECOSYSTEM_CLOUD;
  const { mpacBaseUrl } = BootstrapConfig.get();
  const cloudMigrationInfo = useCloudMigrationInfo(mpacBaseUrl, appKey);

  const analyticsContext = {
    appId,
    partnerId: appDetails?.partner?.id,
    appKey,
    appListingTabKey: tab,
    category: ANALYTICS_CATEGORY_APP_LISTING,
    hosting,
    isEcosystemCloudApp,
    paymentModel,
    alternateCloudAppKey: cloudMigrationInfo.alternateCloudAppKey,
    migrationAssistanceStatus: getMigrationAssistanceStatus(cloudMigrationInfo),
    migrationInformationAvailable: getMigrationInformationAvailable(cloudMigrationInfo),
  };

  return (
    <MarketplaceAnalyticsContext attributes={analyticsContext}>
      <AppListingPageContent {...props} cloudMigrationInfo={cloudMigrationInfo} />
    </MarketplaceAnalyticsContext>
  );
};

export default AppListingWithAnalyticsContext;
